import type {
  Form,
  FormWithFields,
  FormAssignment,
  FormAssignmentWithTitle,
  FormField,
  FormType,
  FormStatus,
} from "@/types/forms";
import type { PaginatedResponse } from "@/types/time";
import { api } from "@/lib/api-client";

// Forms

export async function getForms(params?: {
  type?: FormType;
  status?: FormStatus;
  isArchived?: boolean;
  search?: string;
  page?: number;
  perPage?: number;
}) {
  const query = new URLSearchParams();
  if (params?.type) query.set("type", params.type);
  if (params?.status) query.set("status", params.status);
  if (params?.isArchived !== undefined)
    query.set("is_archived", String(params.isArchived));
  if (params?.search) query.set("search", params.search);
  if (params?.page) query.set("page", String(params.page));
  if (params?.perPage) query.set("per_page", String(params.perPage));
  const qs = query.toString();
  return api<PaginatedResponse<Form>>(`/api/forms${qs ? `?${qs}` : ""}`);
}

export async function getForm(id: string) {
  return api<FormWithFields>(`/api/forms/${id}`);
}

export async function createForm(data: {
  title: string;
  description?: string;
  type: FormType;
  required?: boolean;
}) {
  return api<Form>("/api/forms", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateForm(
  id: string,
  data: { title?: string; description?: string | null; required?: boolean }
) {
  return api<Form>(`/api/forms/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function updateFormStatus(
  id: string,
  data: { status?: FormStatus; isArchived?: boolean }
) {
  return api<Form>(`/api/forms/${id}/status`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// Fields

export async function createField(
  formId: string,
  data: {
    type: string;
    label: string;
    required?: boolean;
    properties?: string;
    validations?: string;
  }
) {
  return api<FormField>(`/api/forms/${formId}/fields`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateField(
  formId: string,
  fieldId: string,
  data: {
    label?: string;
    required?: boolean;
    properties?: string | null;
    validations?: string | null;
  }
) {
  return api<FormField>(`/api/forms/${formId}/fields/${fieldId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteField(formId: string, fieldId: string) {
  return api<FormField>(`/api/forms/${formId}/fields/${fieldId}`, {
    method: "DELETE",
  });
}

export async function reorderFields(formId: string, fieldIds: string[]) {
  return api<{ success: boolean }>(`/api/forms/${formId}/fields/reorder`, {
    method: "POST",
    body: JSON.stringify({ fieldIds }),
  });
}

// Assignments

export async function getAssignments(formId: string, params?: {
  page?: number;
  perPage?: number;
}) {
  const query = new URLSearchParams();
  if (params?.page) query.set("page", String(params.page));
  if (params?.perPage) query.set("per_page", String(params.perPage));
  const qs = query.toString();
  return api<PaginatedResponse<FormAssignment>>(
    `/api/forms/${formId}/assignments${qs ? `?${qs}` : ""}`
  );
}

export async function createAssignment(
  formId: string,
  data: { assignableType: "workspace" | "project" | "task"; assignableId: string }
) {
  return api<FormAssignment>(`/api/forms/${formId}/assignments`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteAssignment(formId: string, assignmentId: string) {
  return api<FormAssignment>(`/api/forms/${formId}/assignments/${assignmentId}`, {
    method: "DELETE",
  });
}

export async function getAssignmentsByTarget(
  assignableType: "project" | "task",
  assignableId: string
) {
  const query = new URLSearchParams({
    assignable_type: assignableType,
    assignable_id: assignableId,
  });
  return api<{
    assignments: FormAssignmentWithTitle[];
  }>(`/api/form-assignments?${query.toString()}`);
}
